import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Package, Plus, Search, Cpu, Trash2, Edit, AlertCircle } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useApplications, useDeleteApplication } from '../../hooks/useApplications'
import { useRole } from '../../hooks/useRole'
import ApplicationModal from '../../components/Modal/ApplicationModal'

export default function Applications() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: applications, isLoading, error } = useApplications()
  const deleteApplication = useDeleteApplication()
  const { canDelete } = useRole()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')

  const statusLabels = {
    Active: { labelKey: 'applications.status.active', color: 'badge-success' },
    Warning: { labelKey: 'applications.status.warning', color: 'badge-warning' },
    Archived: { labelKey: 'applications.status.archived', color: 'badge-gray' },
  }

  const handleAdd = () => {
    setSelectedApplication(null)
    setIsModalOpen(true)
  }

  const handleEdit = (application) => {
    setSelectedApplication(application)
    setIsModalOpen(true)
  }

  const handleDelete = async (id, name) => {
    if (window.confirm(t('applications.removeConfirm', { name }))) {
      try {
        await deleteApplication.mutateAsync(id)
      } catch (err) {
        alert(err.message || t('applications.removeError'))
      }
    }
  }

  const filteredApplications = applications?.filter((app) =>
    app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    app.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">{t('applications.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-600 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {t('applications.loadError')} {error.message}
        </div>
      </div>
    )
  }
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('applications.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">
            {t('applications.subtitle')}
          </p>
        </div>
        <button onClick={handleAdd} className="btn btn-primary">
          <Plus className="w-4 h-4" />
          {t('applications.new')}
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('applications.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 input"
          />
        </div>
        <select className="input w-48">
          <option>{t('applications.filterAll')}</option>
          <option>{t('applications.filterActive')}</option>
          <option>{t('applications.filterWarning')}</option>
        </select>
      </div>

      {/* Applications Grid */}
      {filteredApplications.length === 0 ? (
        <div className="card p-12 text-center">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('applications.empty.none')}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm
              ? t('applications.empty.searchHint')
              : t('applications.empty.firstHint')}
          </p>
          {!searchTerm && (
            <button onClick={handleAdd} className="btn btn-primary">
              <Plus className="w-4 h-4" />
              {t('applications.new')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredApplications.map((app) => (
            <div key={app.id} className="card p-6 cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(`/applications/${app.id}`)}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className="icon-box icon-box-lg" style={{ background: 'var(--theme-icon-box-background, linear-gradient(145deg, var(--theme-gradient-start), var(--theme-gradient-mid)))' , color: 'var(--theme-icon-box-text, #fff)' }}>
                    <Package className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {app.name}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {app.description || t('applications.noDescription')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={clsx(
                    'badge',
                    statusLabels[app.status]?.color || 'badge-gray'
                  )}>
                    {statusLabels[app.status] ? t(statusLabels[app.status].labelKey) : app.status}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">{app.deviceCount || 0}</span> {t('applications.deviceCountLabel')}
                  </span>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEdit(app) }}
                    className="btn btn-secondary btn-sm"
                  >
                    <Edit className="w-4 h-4" />
                    {t('applications.edit')}
                  </button>
                  {canDelete && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(app.id, app.name) }}
                      className="btn btn-error btn-sm"
                      disabled={deleteApplication.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('applications.remove')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      <ApplicationModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setSelectedApplication(null)
        }}
        application={selectedApplication}
      />
    </div>
  )
}
